// Data Connections > Overview tab.
//
// Composite "is everything OK?" dashboard built from existing endpoints:
//   - listIntegrationCatalog() / listIntegrations() - which sources, what state
//   - getDataHealthPipeline()                       - layer cards + pipeline jobs
//   - listLogs()                                    - recent high-signal events
//
// Polling cadence is intentionally slower than the Logs tab (12s); this is
// a decision view, not a live tail.

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ApiHttpError,
  type ApiConfig,
  getDataHealthPipeline,
  listIntegrationCatalog,
  listIntegrations,
  listLogs,
} from "../api";
import {
  cadenceLabel,
  formatMessage,
  formatNotificationState,
  formatSource,
  formatTenant,
  formatTimestamp,
  isNearRealTime,
} from "../format";
import type {
  DataHealthLayerCard,
  DataHealthPipelineResponse,
  DataHealthPipelineRow,
  IntegrationCatalogItem,
  IntegrationState,
  IntegrationStatus,
  LayerStatus,
  PipelineEvent,
  Severity,
} from "../types";

const POLL_INTERVAL_MS = 12_000;
const OVERVIEW_WINDOW_HOURS = 30;
const RECENT_EVENTS_FETCH_LIMIT = 25;
const RECENT_EVENTS_RENDER_LIMIT = 5;
const FAILED_RUN_EVENTS_FETCH_LIMIT = 200;
const STUCK_STREAMING_SECONDS = 30 * 60;
const STUCK_BATCH_SECONDS = 2 * 60 * 60;

// Pipelines that orchestrate other pipelines and do not write data themselves.
// Excluded from record-count aggregation so a parent + producer-children pair
// never double-counts.
const ORCHESTRATOR_PIPELINES = new Set(["etl_pipeline"]);
const CANONICAL_PIPELINE_BY_EVENT = new Map([["raw_to_bronze", "bronze_layer"]]);

export type OverviewNavigationTarget = "logs" | "integrations" | "pipeline";

export function OverviewPanel({
  config,
  onNavigate,
}: {
  config: ApiConfig;
  onNavigate?: (target: OverviewNavigationTarget) => void;
}) {
  const [pipeline, setPipeline] = useState<DataHealthPipelineResponse | null>(null);
  const [integrations, setIntegrations] = useState<IntegrationState[]>([]);
  const [catalog, setCatalog] = useState<IntegrationCatalogItem[]>([]);
  const [recentEvents, setRecentEvents] = useState<PipelineEvent[]>([]);
  const [failedRunEvents, setFailedRunEvents] = useState<PipelineEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const windowStart = overviewWindowStartIso();
      const [pipelineResp, integrationsResp, catalogResp, logsResp, failedLogsResp] = await Promise.all([
        getDataHealthPipeline(config),
        listIntegrations(config),
        listIntegrationCatalog(config),
        listLogs(config, { since: windowStart, limit: RECENT_EVENTS_FETCH_LIMIT }),
        listLogs(config, {
          since: windowStart,
          level: "error",
          limit: FAILED_RUN_EVENTS_FETCH_LIMIT,
        }),
      ]);
      setPipeline(pipelineResp);
      setIntegrations(integrationsResp.data);
      setCatalog(catalogResp.data);
      setRecentEvents(logsResp.data);
      setFailedRunEvents(failedLogsResp.data);
      setError(null);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setLoaded(true);
    }
  }, [config]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const id = window.setInterval(() => {
      void refresh();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [refresh]);

  if (!loaded && !pipeline) {
    return <div className="empty">Loading overview...</div>;
  }
  if (!pipeline) {
    return (
      <div className="ovw-root">
        {error && <div className="error-banner">{error}</div>}
        <div className="empty">Overview data is not available yet.</div>
      </div>
    );
  }

  return (
    <OverviewLayout
      config={config}
      pipeline={pipeline}
      integrations={integrations}
      catalog={catalog}
      recentEvents={recentEvents}
      failedRunEvents={failedRunEvents}
      error={error}
      onNavigate={onNavigate}
    />
  );
}

// ===========================================================================
// Layout
// ===========================================================================
function OverviewLayout({
  config,
  pipeline,
  integrations,
  catalog,
  recentEvents,
  failedRunEvents,
  error,
  onNavigate,
}: {
  config: ApiConfig;
  pipeline: DataHealthPipelineResponse;
  integrations: IntegrationState[];
  catalog: IntegrationCatalogItem[];
  recentEvents: PipelineEvent[];
  failedRunEvents: PipelineEvent[];
  error: string | null;
  onNavigate?: (target: OverviewNavigationTarget) => void;
}) {
  const layers = pipeline.data.layers;
  const rows = pipeline.data.jobs.rows;

  const sourceRowMap = useMemo(() => groupBySource(rows), [rows]);

  const configuredIntegrations = useMemo(
    () => integrations.filter((i) => isConfigured(i.status)),
    [integrations],
  );
  const rejectedIntegrations = useMemo(
    () => integrations.filter((i) => i.status === "rejected"),
    [integrations],
  );
  const failingSources = useMemo(
    () =>
      configuredIntegrations.filter((i) => {
        const sourceRows = sourceRowMap.get(i.source_system) ?? [];
        return sourceRows.some((r) => r.status === "error");
      }),
    [configuredIntegrations, sourceRowMap],
  );
  const onTimeSources = useMemo(
    () =>
      configuredIntegrations.filter((i) => {
        const sourceRows = sourceRowMap.get(i.source_system) ?? [];
        return sourceRows.some(
          (r) => r.status !== "error" && r.last_run.ts_utc !== null,
        );
      }),
    [configuredIntegrations, sourceRowMap],
  );
  const expiringIntegrations = useMemo(
    () => integrations.filter(hasExpiryAction),
    [integrations],
  );
  const stuckPipelineRows = useMemo(() => deriveStuckPipelineRows(rows), [rows]);

  const goldLayer = useMemo(() => layers.find((l) => l.layer_key === "gold"), [layers]);
  const decisionFreshness = useMemo(
    () => deriveDecisionFreshness(rows, goldLayer),
    [rows, goldLayer],
  );
  const failedRuns = useMemo(
    () => deriveFailedRunSummary(failedRunEvents, rows),
    [failedRunEvents, rows],
  );
  const openActions =
    rejectedIntegrations.length +
    failingSources.length +
    stuckPipelineRows.length +
    expiringIntegrations.length;

  const setupSteps = useMemo(
    () => deriveSetupSteps({ integrations, rows, layers }),
    [integrations, rows, layers],
  );
  const headerStatus = useMemo(
    () =>
      deriveHeaderStatus({
        tenantId: config.tenantId,
        failingSources: failingSources.length,
        openActions,
        configuredCount: configuredIntegrations.length,
        rejectedCount: rejectedIntegrations.length,
      }),
    [
      config.tenantId,
      failingSources.length,
      openActions,
      configuredIntegrations.length,
      rejectedIntegrations.length,
    ],
  );

  return (
    <div className="ovw-root">
      {error && <div className="error-banner">{error}</div>}

      <HeaderSummary
        headline={headerStatus.headline}
        sub={headerStatus.sub}
        windowLabel={pipeline.meta.window}
        generatedAt={pipeline.meta.generated_at_utc}
      />

      <KpiRow
        configuredCount={configuredIntegrations.length}
        totalCatalogCount={catalog.length}
        onTimeCount={onTimeSources.length}
        configuredSourcesTotal={configuredIntegrations.length}
        decisionFreshness={decisionFreshness}
        failedRuns={failedRuns}
        openActions={openActions}
      />

      <div className="ovw-dashboard">
        <div className="ovw-main-stack">
          <PipelineFlow
            configuredIntegrations={configuredIntegrations}
            rejectedIntegrations={rejectedIntegrations}
            layers={layers}
            rows={rows}
          />
          <SourceTable
            integrations={integrations}
            sourceRowMap={sourceRowMap}
            onNavigate={onNavigate}
          />
          <SetupProgress steps={setupSteps} />
        </div>
        <div className="ovw-side-stack">
          <ActionQueue
            rejectedIntegrations={rejectedIntegrations}
            failingSources={failingSources}
            stuckPipelineRows={stuckPipelineRows}
            expiringIntegrations={expiringIntegrations}
            onNavigate={onNavigate}
          />
          <RecentActivity events={recentEvents} tenantId={config.tenantId} />
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// Header summary
// ===========================================================================
function HeaderSummary({
  headline,
  sub,
  windowLabel,
  generatedAt,
}: {
  headline: string;
  sub: string;
  windowLabel: string;
  generatedAt: string;
}) {
  return (
    <section className="ovw-head" aria-label="Overview status">
      <div className="ovw-title">
        <h1>{headline}</h1>
        <p>{sub}</p>
      </div>
      <div className="ovw-meta" aria-label="Dashboard metadata">
        <span className="ovw-chip" title={formatTimestamp(generatedAt)}>
          Updated {relativeTime(generatedAt)}
        </span>
        <span className="ovw-chip">Window: {prettyWindow(windowLabel)}</span>
      </div>
    </section>
  );
}

// ===========================================================================
// KPI row
// ===========================================================================
function KpiRow({
  configuredCount,
  totalCatalogCount,
  onTimeCount,
  configuredSourcesTotal,
  decisionFreshness,
  failedRuns,
  openActions,
}: {
  configuredCount: number;
  totalCatalogCount: number;
  onTimeCount: number;
  configuredSourcesTotal: number;
  decisionFreshness: DecisionFreshness;
  failedRuns: FailedRunSummary;
  openActions: number;
}) {
  const connectorTone =
    totalCatalogCount === 0
      ? ""
      : configuredCount === totalCatalogCount
        ? "ok"
        : configuredCount > 0
          ? "warning"
          : "";
  const onTimeTone =
    configuredSourcesTotal === 0
      ? ""
      : onTimeCount === configuredSourcesTotal
        ? "ok"
        : onTimeCount > 0
          ? "warning"
          : "error";
  const openActionsTone = openActions === 0 ? "ok" : "warning";

  return (
    <section aria-label="At-a-glance KPIs">
      <div className="section-title">At a glance</div>
      <div className="ovw-kpis">
        <KpiCard
          label="Connectors"
          value={
            totalCatalogCount === 0 ? "-" : `${configuredCount} / ${totalCatalogCount}`
          }
          tone={connectorTone}
          sub={
            totalCatalogCount === 0
              ? "Catalog not loaded"
              : configuredCount === 0
                ? "No data sources configured yet"
                : `${configuredCount} of ${totalCatalogCount} available connectors configured`
          }
          title="From listIntegrationCatalog() and listIntegrations()"
        />
        <KpiCard
          label="Sources on time"
          value={
            configuredSourcesTotal === 0
              ? "-"
              : `${onTimeCount} / ${configuredSourcesTotal}`
          }
          tone={onTimeTone}
          sub={
            configuredSourcesTotal === 0
              ? "No configured sources yet"
              : onTimeCount === configuredSourcesTotal
                ? "All configured sources have a recent run"
                : `${configuredSourcesTotal - onTimeCount} source(s) without a recent successful run`
          }
          title="On time = at least one non-error pipeline row with last_run in the window"
        />
        <KpiCard
          label="Decision freshness"
          value={decisionFreshness.value}
          tone={decisionFreshness.tone}
          sub={decisionFreshness.sub}
          title={decisionFreshness.title}
        />
        <KpiCard
          label="Failed runs"
          value={String(failedRuns.count)}
          tone={failedRuns.tone}
          sub={failedRuns.sub}
          title={failedRuns.title}
        />
        <KpiCard
          label="Open actions"
          value={String(openActions)}
          tone={openActionsTone}
          sub={
            openActions === 0
              ? "Nothing pending operator review"
              : "See action queue for details"
          }
          title="Rejected integrations + current pipeline failures + stuck runs + API key expiry warnings"
        />
      </div>
    </section>
  );
}

function KpiCard({
  label,
  value,
  sub,
  tone,
  title,
}: {
  label: string;
  value: string;
  sub: string;
  tone?: string;
  title?: string;
}) {
  return (
    <div className="ovw-kpi" title={title}>
      <span className="ovw-kpi-label">{label}</span>
      <span className={"ovw-kpi-value" + (tone ? " " + tone : "")}>{value}</span>
      <span className="ovw-kpi-sub">{sub}</span>
    </div>
  );
}

// ===========================================================================
// Pipeline flow strip
// ===========================================================================
function PipelineFlow({
  configuredIntegrations,
  rejectedIntegrations,
  layers,
  rows,
}: {
  configuredIntegrations: IntegrationState[];
  rejectedIntegrations: IntegrationState[];
  layers: DataHealthLayerCard[];
  rows: DataHealthPipelineRow[];
}) {
  const sourcesStatus: LayerStatus =
    configuredIntegrations.length === 0
      ? "warning"
      : rejectedIntegrations.length > 0
        ? "warning"
        : "healthy";
  const sourcesSub =
    configuredIntegrations.length === 0
      ? "No data sources configured yet."
      : rejectedIntegrations.length > 0
        ? `Rejected credentials on ${joinNames(rejectedIntegrations)}.`
        : `${joinNames(configuredIntegrations)} configured.`;

  const bronze = layers.find((l) => l.layer_key === "bronze");
  const silver = layers.find((l) => l.layer_key === "silver");
  const gold = layers.find((l) => l.layer_key === "gold");

  const flow: FlowStep[] = [
    { key: "sources", name: "Sources", status: sourcesStatus, sub: sourcesSub },
    flowStepFromLayer("raw_ingestion", "Raw Ingestion", bronze, rows, describeBronzeFlow),
    flowStepFromLayer("cleansing", "Cleansing", silver, rows, describeSilverFlow),
    flowStepFromLayer("decision_ready", "Decision-Ready", gold, rows, describeGoldFlow),
  ];

  return (
    <section className="ovw-panel" aria-label="Data flow">
      <div className="ovw-panel-head">
        <h2>Pipeline flow</h2>
        <span className="ovw-panel-meta">Product-facing layer names</span>
      </div>
      <div className="ovw-flow">
        <div className="ovw-flow-strip">
          {flow.map((step, i) => (
            <FlowSegment key={step.key} step={step} showArrow={i < flow.length - 1} />
          ))}
        </div>
      </div>
    </section>
  );
}

interface FlowStep {
  key: string;
  name: string;
  status: LayerStatus;
  sub: string;
}

function flowStepFromLayer(
  key: string,
  name: string,
  layer: DataHealthLayerCard | undefined,
  rows: DataHealthPipelineRow[],
  describe: (l: DataHealthLayerCard) => string,
): FlowStep {
  if (!layer) {
    return { key, name, status: "warning", sub: `${name} summary unavailable.` };
  }
  if (layer.status === "healthy") {
    return { key, name, status: "healthy", sub: describe(layer) };
  }
  return { key, name, status: layer.status, sub: describeLayerIssue(layer, rows) };
}

/** Surface the offending pipeline(s) so the operator can see WHY a layer is warning/error. */
function describeLayerIssue(
  layer: DataHealthLayerCard,
  rows: DataHealthPipelineRow[],
): string {
  const offending = rows.filter(
    (r) =>
      r.route.includes(layer.display_name) &&
      (r.status === "warning" || r.status === "error"),
  );
  if (offending.length === 0) {
    return `${layer.display_name} reports ${layer.status} with no row-level signal.`;
  }
  // Group by status so we can say "X warning, Y error" honestly.
  const byStatus = new Map<string, DataHealthPipelineRow[]>();
  for (const r of offending) {
    const list = byStatus.get(r.status) ?? [];
    list.push(r);
    byStatus.set(r.status, list);
  }
  const parts: string[] = [];
  for (const status of ["error", "warning"] as const) {
    const list = byStatus.get(status);
    if (!list || list.length === 0) continue;
    // Multiple pipelines (e.g. bronze_layer + raw_to_bronze) can share the
    // same display label - prefix the source so "Bronze Layer, Bronze Layer
    // error." reads as "Magento Bronze Layer error." and dedupe in case the
    // same source+label pair still collides.
    const labels = Array.from(
      new Set(list.map((r) => `${r.route[0]} ${r.pipeline_label}`)),
    );
    const shown = labels.slice(0, 2);
    const more = labels.length > 2 ? ` (+${labels.length - 2} more)` : "";
    parts.push(`${shown.join(", ")}${more} ${status}`);
  }
  return parts.join(" - ") + ".";
}

function FlowSegment({ step, showArrow }: { step: FlowStep; showArrow: boolean }) {
  return (
    <>
      <div className={"ovw-flow-card " + step.status}>
        <div className="ovw-flow-name">
          <span>{step.name}</span>
          <span className={"ovw-pill " + step.status}>{layerStatusLabel(step.status)}</span>
        </div>
        <div className="ovw-flow-sub">{step.sub}</div>
      </div>
      {showArrow && <div className="ovw-flow-arrow">-&gt;</div>}
    </>
  );
}

function describeBronzeFlow(layer: DataHealthLayerCard): string {
  const rc = layer.totals.record_count;
  if (rc != null) return `${formatCompactInt(rc)} records ingested across ${layer.jobs.label}.`;
  return `${layer.jobs.label} active.`;
}

function describeSilverFlow(layer: DataHealthLayerCard): string {
  return `${layer.jobs.label} available for cleansing and normalization.`;
}

function describeGoldFlow(layer: DataHealthLayerCard): string {
  return `${layer.jobs.label} serving downstream use cases.`;
}

// ===========================================================================
// Source table
// ===========================================================================
function SourceTable({
  integrations,
  sourceRowMap,
  onNavigate,
}: {
  integrations: IntegrationState[];
  sourceRowMap: Map<string, DataHealthPipelineRow[]>;
  onNavigate?: (target: OverviewNavigationTarget) => void;
}) {
  return (
    <section className="ovw-panel" aria-label="Source health">
      <div className="ovw-panel-head">
        <h2>Sources</h2>
        <span className="ovw-panel-meta">Connector + freshness + impact</span>
      </div>
      {integrations.length === 0 ? (
        <div className="empty">No source integrations available.</div>
      ) : (
        <div className="ovw-source-table" role="table">
          <div className="ovw-source-row head" role="row">
            <div role="columnheader">Source</div>
            <div role="columnheader">Connection</div>
            <div role="columnheader">Last success</div>
            <div role="columnheader">Expected cadence</div>
            <div role="columnheader">Data impact</div>
            <div role="columnheader">Action</div>
          </div>
          {integrations.map((state) => (
            <SourceRow
              key={state.source_system}
              state={state}
              rows={sourceRowMap.get(state.source_system) ?? []}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function SourceRow({
  state,
  rows,
  onNavigate,
}: {
  state: IntegrationState;
  rows: DataHealthPipelineRow[];
  onNavigate?: (target: OverviewNavigationTarget) => void;
}) {
  const lastSuccess = pickLastSuccessRow(rows);
  const lastSuccessLabel = lastSuccess?.last_run.label ?? "-";
  const lastSuccessSub = lastSuccess
    ? lastSuccess.pipeline_label
    : isConfigured(state.status)
      ? "Awaiting first run"
      : "Not configured";
  const cadenceText = describeCadence(rows);
  const action = describeAction(state.status);

  return (
    <div className="ovw-source-row" role="row">
      <div role="cell">
        <div className="ovw-src-name">{state.display_name}</div>
        <div className="ovw-src-sub">{describeCategory(state.category)}</div>
      </div>
      <div role="cell">
        <span className={"ovw-pill " + integrationStatusTone(state.status)}>
          {integrationStatusLabel(state.status)}
        </span>
      </div>
      <div role="cell">
        <div>{lastSuccessLabel}</div>
        <div className="ovw-src-sub">{lastSuccessSub}</div>
      </div>
      <div role="cell">{cadenceText}</div>
      <div role="cell">
        <div>{describeDataImpactPrimary(rows, state.status)}</div>
        <div className="ovw-src-sub">{describeDataImpactSub(rows, state.status)}</div>
      </div>
      <div role="cell">
        <button
          className="ovw-src-action"
          onClick={() => onNavigate?.(action.target)}
          type="button"
        >
          {action.label}
        </button>
      </div>
    </div>
  );
}

// ===========================================================================
// Setup progress
// ===========================================================================
type SetupStepState = "done" | "in_progress" | "pending";

function SetupProgress({
  steps,
}: {
  steps: { label: string; meta: string; state: SetupStepState }[];
}) {
  return (
    <section className="ovw-panel" aria-label="Tenant setup progress">
      <div className="ovw-panel-head">
        <h2>Tenant setup progress</h2>
        <span className="ovw-panel-meta">Derived from connectors and pipeline activity</span>
      </div>
      <div className="ovw-setup">
        {steps.map((step, i) => (
          <div className="ovw-setup-step" key={step.label}>
            <span className={"ovw-step-dot " + step.state}>{stepGlyph(step.state, i)}</span>
            <span className="ovw-step-label">{step.label}</span>
            <span className="ovw-step-meta">{step.meta}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function stepGlyph(state: SetupStepState, i: number): string {
  if (state === "done") return "OK";
  return String(i + 1);
}

// ===========================================================================
// Action queue
// ===========================================================================
function ActionQueue({
  rejectedIntegrations,
  failingSources,
  stuckPipelineRows,
  expiringIntegrations,
  onNavigate,
}: {
  rejectedIntegrations: IntegrationState[];
  failingSources: IntegrationState[];
  stuckPipelineRows: DataHealthPipelineRow[];
  expiringIntegrations: IntegrationState[];
  onNavigate?: (target: OverviewNavigationTarget) => void;
}) {
  const items: ActionItem[] = [
    ...rejectedIntegrations.map(
      (i): ActionItem => ({
        key: "rejected@" + i.source_system,
        kind: "error",
        title: `Refresh ${i.display_name} credentials`,
        tone: "error",
        toneLabel: "Credentials",
        copy:
          "The source rejected the submitted credentials. Re-validate on the Integrations tab to resume pipelines.",
        actions: [
          { label: "Open Integrations", primary: true, target: "integrations" },
          { label: "View Logs", target: "logs" },
        ],
      }),
    ),
    ...failingSources.map(
      (i): ActionItem => ({
        key: "failing@" + i.source_system,
        kind: "error",
        title: `${i.display_name} pipeline is failing`,
        tone: "error",
        toneLabel: "Pipeline",
        copy:
          "The latest Data Health state for this source is still failing. Open Data Health for the failing step.",
        actions: [
          { label: "Open Data Health", primary: true, target: "pipeline" },
          { label: "View Logs", target: "logs" },
        ],
      }),
    ),
    ...stuckPipelineRows.map(
      (r): ActionItem => ({
        key: "stuck@" + r.source_system + "@" + r.pipeline,
        kind: "warning",
        title: `${r.pipeline_label} has been running too long`,
        tone: "attention",
        toneLabel: "Stuck run",
        copy:
          `${formatSource(r.source_system)} ${r.pipeline_label} has been running for ${runningAgeLabel(r)}. ` +
          "Check Data Health before starting another run.",
        actions: [
          { label: "Open Data Health", primary: true, target: "pipeline" },
          { label: "View Logs", target: "logs" },
        ],
      }),
    ),
    ...expiringIntegrations.map(
      (i): ActionItem => {
        const state = i.expiry.notification_state;
        const expired = state === "expired";
        const copy =
          formatNotificationState(state, i.expiry.days_remaining) ??
          "API key expiry needs operator review.";
        return {
          key: "expiry@" + i.source_system,
          kind: expired ? "error" : "warning",
          title: `Rotate ${i.display_name} API key`,
          tone: expired ? "error" : "attention",
          toneLabel: expired ? "Expired" : "Expiry",
          copy,
          actions: [
            { label: "Open Integrations", primary: true, target: "integrations" },
          ],
        };
      },
    ),
  ];

  return (
    <section className="ovw-panel" aria-label="Active actions">
      <div className="ovw-panel-head">
        <h2>Action queue</h2>
        <span className="ovw-panel-meta">Ranked by operator impact</span>
      </div>
      <div className="ovw-issue-list">
        {items.length === 0 ? (
          <div className="ovw-issue-row healthy">
            <span className="ovw-issue-mark healthy">OK</span>
            <div>
              <div className="ovw-issue-title">
                <span>No open actions</span>
                <span className="ovw-pill healthy">Clear</span>
              </div>
              <div className="ovw-issue-copy">
                No rejected credentials, current pipeline failures, stuck runs, or API key expiry warnings.
              </div>
            </div>
          </div>
        ) : (
          items.map((it) => (
            <div key={it.key} className={"ovw-issue-row " + it.kind}>
              <span className={"ovw-issue-mark " + it.kind}>!</span>
              <div>
                <div className="ovw-issue-title">
                  <span>{it.title}</span>
                  <span className={"ovw-pill " + it.tone}>{it.toneLabel}</span>
                </div>
                <div className="ovw-issue-copy">{it.copy}</div>
                <div className="ovw-issue-actions">
                  {it.actions.map((a) => (
                    <button
                      key={a.label}
                      type="button"
                      className={"ovw-small-btn" + (a.primary ? " primary" : "")}
                      onClick={() => onNavigate?.(a.target)}
                    >
                      {a.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
}

interface ActionItem {
  key: string;
  kind: "warning" | "error";
  title: string;
  tone: string;
  toneLabel: string;
  copy: string;
  actions: { label: string; primary?: boolean; target: OverviewNavigationTarget }[];
}

// ===========================================================================
// Recent activity
// ===========================================================================
function RecentActivity({
  events,
  tenantId,
}: {
  events: PipelineEvent[];
  tenantId: string;
}) {
  const filtered = useMemo(() => filterHighSignalEvents(events), [events]);

  return (
    <section className="ovw-panel" aria-label="Recent important activity">
      <div className="ovw-panel-head">
        <h2>Recent activity</h2>
        <span className="ovw-panel-meta">High-signal only</span>
      </div>
      <div className="ovw-activity">
        {filtered.length === 0 ? (
          <div className="empty">No recent high-signal events.</div>
        ) : (
          filtered.map((e) => (
            <div className="ovw-activity-row" key={e.event_id}>
              <div className="ovw-activity-meta">
                <span className="ovw-activity-time" title={formatTimestamp(e.ts_utc)}>
                  {relativeTime(e.ts_utc)}
                </span>
                <span className="ovw-activity-source">{formatSource(e.source_system)}</span>
              </div>
              <span className="ovw-activity-message" title={formatMessage(e.message, tenantId)}>
                {formatMessage(e.message, tenantId)}
              </span>
              <span className={"ovw-pill " + severityTone(e.severity)}>
                {severityShortLabel(e.severity)}
              </span>
            </div>
          ))
        )}
      </div>
    </section>
  );
}

// ===========================================================================
// Helpers
// ===========================================================================
function groupBySource(rows: DataHealthPipelineRow[]): Map<string, DataHealthPipelineRow[]> {
  const map = new Map<string, DataHealthPipelineRow[]>();
  for (const r of rows) {
    const list = map.get(r.source_system) ?? [];
    list.push(r);
    map.set(r.source_system, list);
  }
  return map;
}

function isConfigured(status: IntegrationStatus | string | undefined): boolean {
  return status === "pending_operator_setup" || status === "operator_completed";
}

function hasExpiryAction(integration: IntegrationState): boolean {
  const state = integration.expiry?.notification_state;
  return (
    integration.status !== "rejected" &&
    integration.expiry?.tracking_enabled === true &&
    state !== null &&
    state !== undefined &&
    state !== "active"
  );
}

function deriveStuckPipelineRows(rows: DataHealthPipelineRow[]): DataHealthPipelineRow[] {
  const nowMs = Date.now();
  return rows.filter((row) => {
    if (row.status !== "running") return false;
    if (ORCHESTRATOR_PIPELINES.has(row.pipeline)) return false;
    const startedMs = parseTimestamp(row.last_run.ts_utc);
    if (startedMs === null) return false;
    return (nowMs - startedMs) / 1000 >= stuckThresholdSeconds(row);
  });
}

function stuckThresholdSeconds(row: DataHealthPipelineRow): number {
  return isNearRealTime(row.cadence, row.pipeline)
    ? STUCK_STREAMING_SECONDS
    : STUCK_BATCH_SECONDS;
}

function runningAgeLabel(row: DataHealthPipelineRow): string {
  const startedMs = parseTimestamp(row.last_run.ts_utc);
  if (startedMs === null) return "longer than expected";
  return formatDurationCompact((Date.now() - startedMs) / 1000);
}

function integrationStatusTone(status: IntegrationStatus | string): string {
  if (status === "operator_completed" || status === "pending_operator_setup") {
    return "configured";
  }
  if (status === "rejected") return "error";
  return "idle";
}

function integrationStatusLabel(status: IntegrationStatus | string): string {
  if (status === "operator_completed") return "Configured";
  if (status === "pending_operator_setup") return "Setup pending";
  if (status === "rejected") return "Needs attention";
  return "Not configured";
}

function describeCategory(category: string): string {
  if (!category) return "";
  return category.charAt(0).toUpperCase() + category.slice(1);
}

function pickLastSuccessRow(rows: DataHealthPipelineRow[]): DataHealthPipelineRow | undefined {
  const candidates = rows.filter(
    (r) =>
      r.last_run.ts_utc !== null &&
      r.status !== "error" &&
      !ORCHESTRATOR_PIPELINES.has(r.pipeline),
  );
  if (candidates.length === 0) {
    // Fall back to orchestrator rows so the operator at least sees a timestamp.
    const fallback = rows
      .filter((r) => r.last_run.ts_utc !== null && r.status !== "error")
      .sort((a, b) => (b.last_run.ts_utc ?? "").localeCompare(a.last_run.ts_utc ?? ""));
    return fallback[0];
  }
  candidates.sort((a, b) => (b.last_run.ts_utc ?? "").localeCompare(a.last_run.ts_utc ?? ""));
  return candidates[0];
}

interface DecisionFreshness {
  value: string;
  sub: string;
  tone?: string;
  title: string;
}

interface FailedRunSummary {
  count: number;
  sub: string;
  tone?: string;
  title: string;
}

function deriveFailedRunSummary(
  events: PipelineEvent[],
  rows: DataHealthPipelineRow[],
): FailedRunSummary {
  const failedRuns = events
    .filter(isCountedFailedRun)
    .sort((a, b) => b.ts_utc.localeCompare(a.ts_utc));

  if (failedRuns.length === 0) {
    return {
      count: 0,
      sub: `No failed executions in last ${OVERVIEW_WINDOW_HOURS}h`,
      tone: "ok",
      title: "Count of non-orchestrator PipelineRunFailed log events in the last 30h.",
    };
  }

  const latest = failedRuns[0];
  const currentRow = findCurrentRowForEvent(latest, rows);
  const latestRecovered = isRecoveredAfterFailure(latest, currentRow);
  const latestRunning = isRunningAfterFailure(latest, currentRow);
  const latestStillFailing = currentRow?.status === "error";
  const countLabel = `${failedRuns.length} failed execution${failedRuns.length === 1 ? "" : "s"}`;
  const latestLabel = latestRecovered
    ? "latest recovered"
    : latestRunning
      ? "rerun in progress"
      : latestStillFailing
        ? "latest still failing"
        : "latest unresolved";

  return {
    count: failedRuns.length,
    sub: `${countLabel} in last ${OVERVIEW_WINDOW_HOURS}h, ${latestLabel}`,
    tone: latestStillFailing ? "error" : "warning",
    title:
      "Counts non-orchestrator PipelineRunFailed log events from the last 30h. " +
      "Parent orchestrator failures are excluded so one child failure is not double-counted.",
  };
}

function isCountedFailedRun(event: PipelineEvent): boolean {
  return (
    event.event_type === "PipelineRunFailed" &&
    !ORCHESTRATOR_PIPELINES.has(event.pipeline)
  );
}

function findCurrentRowForEvent(
  event: PipelineEvent,
  rows: DataHealthPipelineRow[],
): DataHealthPipelineRow | undefined {
  const source = event.source_system;
  const pipeline = canonicalPipeline(event.pipeline);
  return rows.find(
    (r) => r.source_system === source && canonicalPipeline(r.pipeline) === pipeline,
  );
}

function canonicalPipeline(pipeline: string): string {
  return CANONICAL_PIPELINE_BY_EVENT.get(pipeline) ?? pipeline;
}

function isRecoveredAfterFailure(
  event: PipelineEvent,
  row: DataHealthPipelineRow | undefined,
): boolean {
  if (!row || row.status === "error" || row.status === "running") return false;
  return isAfter(row.last_run.ts_utc, event.ts_utc);
}

function isRunningAfterFailure(
  event: PipelineEvent,
  row: DataHealthPipelineRow | undefined,
): boolean {
  return row?.status === "running" && isAfter(row.last_run.ts_utc, event.ts_utc);
}

function deriveDecisionFreshness(
  rows: DataHealthPipelineRow[],
  decisionLayer: DataHealthLayerCard | undefined,
): DecisionFreshness {
  if (!decisionLayer) {
    return {
      value: "-",
      sub: "Decision-Ready layer summary unavailable",
      title: "Data Health did not return a Decision-Ready layer card.",
    };
  }

  const decisionRows = rows.filter((r) => r.route.includes(decisionLayer.display_name));
  const completedRows = decisionRows.filter(
    (r) =>
      r.status !== "error" &&
      r.status !== "running" &&
      r.lag.seconds !== null,
  );

  if (completedRows.length === 0) {
    const running = decisionRows.some((r) => r.status === "running");
    return {
      value: running ? "Running" : "-",
      sub: running
        ? "Decision-Ready refresh in progress"
        : "No Decision-Ready refresh in window",
      tone: running ? "ok" : "warning",
      title: `Computed from non-error pipeline rows that reach ${decisionLayer.display_name}.`,
    };
  }

  const oldest = completedRows.reduce((max, row) =>
    (row.lag.seconds ?? 0) > (max.lag.seconds ?? 0) ? row : max,
  );

  return {
    value: formatDurationCompact(oldest.lag.seconds ?? 0),
    sub: "Oldest Decision-Ready output refresh",
    tone: completedRows.some((r) => r.status === "warning") ? "warning" : "ok",
    title:
      `Oldest non-error pipeline row reaching ${decisionLayer.display_name}: ` +
      `${oldest.pipeline_label} refreshed ${oldest.last_run.label}.`,
  };
}

function describeCadence(rows: DataHealthPipelineRow[]): string {
  if (rows.length === 0) return "-";
  // Prefer a near-real-time row's live label ("Every 90 min"); otherwise use
  // the first row's label ("Daily"/"Batch"). Falls back to the slug heuristic
  // inside cadenceLabel when the API did not resolve a cadence.
  const nearReal = rows.find((r) => isNearRealTime(r.cadence, r.pipeline));
  const chosen = nearReal ?? rows[0];
  return cadenceLabel(chosen.cadence, chosen.pipeline);
}

function describeDataImpactPrimary(
  rows: DataHealthPipelineRow[],
  status: IntegrationStatus | string,
): string {
  if (!isConfigured(status)) return "Not active";
  if (rows.length === 0) return "Awaiting activity";
  if (rows.some((r) => r.status === "error")) return "Failure detected";
  if (rows.some((r) => r.status === "warning")) return "With warnings";
  return "Current";
}

function describeDataImpactSub(
  rows: DataHealthPipelineRow[],
  status: IntegrationStatus | string,
): string {
  if (!isConfigured(status)) return "No pipelines for this source.";
  if (rows.length === 0) return "No recent runs in the window.";
  const errors = rows.filter((r) => r.status === "error").length;
  if (errors > 0) return `${errors} failing pipeline${errors > 1 ? "s" : ""}.`;
  return "Producer rows reporting successful runs.";
}

function describeAction(
  status: IntegrationStatus | string,
): { label: string; target: OverviewNavigationTarget } {
  if (status === "rejected") return { label: "Refresh credentials", target: "integrations" };
  if (status === "not_configured") return { label: "Configure", target: "integrations" };
  return { label: "View health", target: "pipeline" };
}

function layerStatusLabel(status: LayerStatus): string {
  if (status === "healthy") return "Healthy";
  if (status === "warning") return "Warning";
  return "Error";
}

function deriveSetupSteps({
  integrations,
  rows,
  layers,
}: {
  integrations: IntegrationState[];
  rows: DataHealthPipelineRow[];
  layers: DataHealthLayerCard[];
}): { label: string; meta: string; state: SetupStepState }[] {
  const submitted = integrations.filter((i) => i.status !== "not_configured");
  const validated = integrations.filter((i) =>
    ["pending_operator_setup", "operator_completed"].includes(i.status),
  );
  const operatorComplete = integrations.filter((i) => i.status === "operator_completed");
  const anyRun = rows.some((r) => r.last_run.ts_utc !== null);
  const anyData = layers.some((l) => (l.totals.record_count ?? 0) > 0);

  const flags = [
    submitted.length > 0,
    validated.length > 0,
    operatorComplete.length > 0,
    anyRun,
    anyData,
  ];
  const states: SetupStepState[] = flags.map((done, i) => {
    if (done) return "done";
    if (i === 0 || flags[i - 1]) return "in_progress";
    return "pending";
  });

  const submittedNames = joinNames(submitted) || "None yet";
  const validatedNames = joinNames(validated) || "Pending";
  const operatorNames = joinNames(operatorComplete) || "Pending";

  return [
    {
      label: "Credentials received",
      meta: submitted.length === 0 ? "Awaiting first connector" : submittedNames,
      state: states[0],
    },
    {
      label: "Validated",
      meta: validated.length === 0 ? "Awaiting validation" : `${validatedNames} validated`,
      state: states[1],
    },
    {
      label: "Operator setup",
      meta:
        operatorComplete.length === 0
          ? "Awaiting operator provisioning"
          : `${operatorNames} provisioned`,
      state: states[2],
    },
    {
      label: "First run",
      meta: anyRun ? "Pipeline runs detected in window" : "No runs in window yet",
      state: states[3],
    },
    {
      label: "Data available",
      meta: anyData ? "Records present in at least one layer" : "Storage empty",
      state: states[4],
    },
  ];
}

function deriveHeaderStatus({
  tenantId,
  failingSources,
  openActions,
  configuredCount,
  rejectedCount,
}: {
  tenantId: string;
  failingSources: number;
  openActions: number;
  configuredCount: number;
  rejectedCount: number;
}): { headline: string; sub: string } {
  const tenant = formatTenant(tenantId);
  if (configuredCount === 0 && rejectedCount === 0) {
    return {
      headline: `No data sources configured for ${tenant}`,
      sub: "Configure a connector on the Integrations tab to start the pipeline.",
    };
  }
  if (failingSources > 0) {
    return {
      headline: `Pipeline needs attention for ${tenant}`,
      sub: `${failingSources} source pipeline${failingSources > 1 ? "s are" : " is"} currently failing.`,
    };
  }
  if (openActions > 0) {
    return {
      headline: `Data is flowing for ${tenant}`,
      sub: `${openActions} operator action${openActions > 1 ? "s" : ""} pending. Pipeline runs are otherwise healthy.`,
    };
  }
  return {
    headline: `Data is flowing for ${tenant}`,
    sub: "All configured sources have a recent successful run.",
  };
}

function filterHighSignalEvents(events: PipelineEvent[]): PipelineEvent[] {
  const filtered = events.filter((e) => {
    if (e.severity !== "INFO") return true;
    // Drop pure UI Started markers; keep completion-level info events.
    return e.event_type !== "PipelineRunStarted";
  });
  return filtered.slice(0, RECENT_EVENTS_RENDER_LIMIT);
}

function severityTone(severity: Severity): "healthy" | "warning" | "error" {
  if (severity === "ERROR") return "error";
  if (severity === "WARNING") return "warning";
  return "healthy";
}

function severityShortLabel(severity: Severity): string {
  if (severity === "ERROR") return "Failed";
  if (severity === "WARNING") return "Warning";
  return "Done";
}

function joinNames(list: { display_name: string }[]): string {
  if (list.length === 0) return "";
  if (list.length === 1) return list[0].display_name;
  if (list.length === 2) return `${list[0].display_name} and ${list[1].display_name}`;
  return list.slice(0, -1).map((i) => i.display_name).join(", ") + ", and " + list[list.length - 1].display_name;
}

function formatCompactInt(value: number): string {
  if (!Number.isFinite(value)) return String(value);
  const rounded = Math.round(value);
  if (rounded >= 1_000_000_000) {
    return (rounded / 1_000_000_000).toFixed(1).replace(/\.0$/, "") + "B";
  }
  if (rounded >= 1_000_000) {
    return (rounded / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  }
  if (rounded >= 1_000) {
    return (rounded / 1_000).toFixed(1).replace(/\.0$/, "") + "K";
  }
  return rounded.toLocaleString();
}

function formatDurationCompact(seconds: number): string {
  if (!Number.isFinite(seconds)) return "-";
  const wholeSeconds = Math.max(0, Math.floor(seconds));
  if (wholeSeconds < 60) return `${wholeSeconds}s`;
  const minutes = Math.floor(wholeSeconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

function prettyWindow(windowKey: string): string {
  if (windowKey === "last_30h") return "last 30h";
  return windowKey.replace(/_/g, " ");
}

function overviewWindowStartIso(nowMs: number = Date.now()): string {
  return new Date(nowMs - OVERVIEW_WINDOW_HOURS * 60 * 60 * 1000).toISOString();
}

function isAfter(candidateIso: string | null | undefined, referenceIso: string): boolean {
  const candidate = parseTimestamp(candidateIso);
  const reference = parseTimestamp(referenceIso);
  return candidate !== null && reference !== null && candidate > reference;
}

function parseTimestamp(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const value = new Date(iso).getTime();
  return Number.isNaN(value) ? null : value;
}

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return "-";
  const then = new Date(iso).getTime();
  if (isNaN(then)) return iso;
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatError(e: unknown): string {
  if (e instanceof ApiHttpError) {
    return `${e.status} ${e.code}: ${e.message}${e.requestId ? ` (req ${e.requestId})` : ""}`;
  }
  if (e instanceof Error) return e.message;
  return String(e);
}
